<template>
  <div class="ap-info" role="dialog" :aria-label="info.title">
    <div class="ap-info__head">
      <span class="material-icons ap-info__icon" :style="{ color: ACTIVITY_TAB_STYLES[tab].color }">{{ ACTIVITY_TAB_STYLES[tab].icon }}</span>
      <h3 class="ap-info__title">{{ info.title }}</h3>
      <button type="button" class="ap-info__close" :aria-label="$t('common.close')" @click="emit('close')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <p class="ap-info__summary">{{ info.summary }}</p>
    <ul class="ap-info__rows">
      <li v-for="row in info.rows" :key="row.body" class="ap-info__row">
        <span
          class="ap-info__art"
          :class="`ap-info__art--${row.art}`"
          :style="row.art === 'line' ? { background: lineGradient(info.lineColors) } : undefined"
          aria-hidden="true"
        />
        <p class="ap-info__text"><strong>{{ row.lead }}</strong> {{ row.body }}</p>
      </li>
    </ul>
    <p class="ap-info__hint">{{ $t('recordings.activity.chart_info.hover_hint') }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { chartInfo, lineGradient } from '../../../utils/recordingActivity/chartInfo';
import type { ActivityTab } from '../../../utils/recordingActivity/recordingActivity';
import { ACTIVITY_TAB_STYLES } from '../../../utils/recordingActivity/tabStyles';

const props = defineProps<{ tab: ActivityTab }>();

const emit = defineEmits<{ (e: 'close'): void }>();

const { t } = useI18n();
const info = computed(() => chartInfo(props.tab, t));
</script>

<style scoped>
.ap-info {
  padding: 20px;
  color: #fff;
  background: rgba(40, 42, 50, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
}

.ap-info__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ap-info__icon {
  font-size: 20px;
}

.ap-info__title {
  flex: 1;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.ap-info__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 50%;
}

.ap-info__close:hover {
  background: rgba(255, 255, 255, 0.16);
}

.ap-info__close .material-icons {
  font-size: 16px;
}

.ap-info__summary {
  margin: 16px 0;
  font-size: 0.95rem;
}

.ap-info__rows {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ap-info__row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ap-info__art {
  flex-shrink: 0;
  width: 48px;
}

.ap-info__art--line {
  height: 8px;
  border-radius: 999px;
}

.ap-info__art--shape {
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  clip-path: polygon(0 70%, 100% 0, 100% 100%, 0 100%);
}

.ap-info__text {
  margin: 0;
  font-size: 0.9rem;
  color: #acb0bf;
}

.ap-info__text strong {
  color: #fff;
}

.ap-info__hint {
  margin: 16px 0 0;
  padding-top: 14px;
  font-size: 0.8rem;
  color: #acb0bf;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
